import React, {useEffect, useState} from 'react';
import GrayModal from '../../../../../ui-kit/src/dataDisplay/GrayModal';
import {ErrorAlert} from '../../../../../ui-kit/src/commons/errors';
import Loading from '../../../../../ui-kit/src/atoms/Loading';
import {Badge, Table} from 'reactstrap';
import Pill from '../../../../../ui-kit/src/atoms/Pill';
import ProductImagePlaceholder from '../../../../../ui-kit/src/molecules/products/ProductImagePlaceholder';
import NoContentAvailable from '../../../../../ui-kit/src/dataDisplay/NoContentAvailable';
import GrayTableHeader, {GrayTableHeaderCell} from '../../../../../ui-kit/src/molecules/tables/GrayTableHeader';
import SimpleTableRow, {SimpleTableCell} from '../../../../../ui-kit/src/molecules/tables/SimpleTableRow';
import {ourToast} from "../../../../../ui-kit/src/atoms/Toast";
import {HttpService} from "../../../../../ui-kit/src/commons/http";
import styles from "./ImagesModal.module.scss"


type ImageLinks = {
  hi_res: string | null,
  image: string | null,
  thumbnail_small: string | null,
  thumbnail_medium: string | null,
  source: string | null,
}

type ImageItem = {
  id: string,
  group_id: number,
  orientation: string,
  type: string,
  size_category: string,
  tags: Record<string, string>,
  links: ImageLinks,
  cached_ai_labels: Record<string, unknown> | null,
}

type ResolvedImageItem = ImageItem & {
  unified_product_id: string | null,
  product_id: string | null,
  group_style_number: string | null,
  group_color_name: string | null,
  group_color_code: string | null,
  group_orientation: string | null,
  group_collection_id: number | null,
  is_borrowed: boolean,
  borrowed_from_unified_product_id: string | null,
}

type ImageGroup = {
  id: number,
  style_number: string,
  color_name: string,
  color_code: string,
}

type ImagesApiResponse = {
  status: string,
  gtin: string | null,
  gtin_centralized_images_active: boolean,
  groups: ImageGroup[],
  images: ImageItem[],
  resolved_images: ResolvedImageItem[],
}


function LinkIndicator({href, label, withCopy = false}: {
  href: string | null,
  label: string,
  withCopy?: boolean
}) {
  const onCopy = () => {
    if (typeof href === 'string') {
      navigator.clipboard.writeText(href);
      ourToast("success", "Copied!");
    }
  }

  if (href) {
    return <div className={styles.linkItem}>
      <i className={'fa-solid fa-circle-check text-success mr-1'} style={{fontSize: '0.75rem'}}/>
      <a href={href} target={'_blank'} className={styles.linkLabel}>{label}</a>
      {withCopy && <i className={'fa-light fa-copy ml-1'} style={{cursor: "pointer", fontSize: '0.75rem'}} onClick={onCopy}
                      title={"Copy to clipboard"}/>}
    </div>
  } else {
    return <div className={styles.linkItem}>
      <i className={'fa-solid fa-circle-xmark text-muted mr-1'} style={{fontSize: '0.75rem'}}/>
      <span className={'text-muted'}>{label}</span>
    </div>
  }
}


function TagsDisplay({tags}: {tags: unknown}) {
  let parsed: Record<string, string> = {};
  try {
    if (typeof tags === 'string') {
      parsed = JSON.parse(tags);
    } else if (typeof tags === 'object' && tags !== null) {
      parsed = tags as Record<string, string>;
    }
  } catch {
    return <span className="text-muted">Invalid tags</span>;
  }

  const entries = Object.entries(parsed);
  if (entries.length === 0) {
    return <span className="text-muted">No tags</span>;
  }

  return <div className={styles.tagsGrid}>
    {entries.map(([key, value]) => (
      <div key={key} className={styles.tagRow}>
        <span className={styles.tagKey}>{key}</span>
        <span className={styles.tagValue}>{String(value)}</span>
      </div>
    ))}
  </div>
}


function AiLabelsDisplay({labels}: {labels: Record<string, unknown> | null | undefined}) {
  if (!labels || Object.keys(labels).length === 0) {
    return <span className="text-muted font-italic">No AI labels cached</span>;
  }

  const entries = Object.entries(labels);
  return <div className={styles.tagsGrid}>
    {entries.map(([key, value]) => (
      <div key={key} className={styles.tagRow}>
        <span className={styles.tagKey}>{key}</span>
        <span className={styles.tagValue}>{String(value)}</span>
      </div>
    ))}
  </div>
}


export function buildEditProductUrl(productId: string, unifiedProductId: string | null) {
  // Colons are valid inside a path segment / query value, so keep the system IDs readable
  const base = `/product/catalog/editProduct/${productId}`;
  return unifiedProductId ? `${base}?unified=${unifiedProductId}` : base;
}


function OriginIndicator({img}: {img: ResolvedImageItem}) {
  if (!img.is_borrowed) {
    return <Badge color={'success'} className={styles.originBadge}>Own</Badge>;
  }

  const badge = <Badge color={'warning'} className={styles.originBadge}>
    Borrowed
    <i className={'fa-solid fa-arrow-up-right-from-square ml-1'} style={{fontSize: '0.6rem'}}/>
  </Badge>;

  const sourceUnifiedId = img.borrowed_from_unified_product_id || img.unified_product_id;
  if (!img.product_id) {
    return badge;
  }

  return <a href={buildEditProductUrl(img.product_id, sourceUnifiedId)}
            target={'_blank'}
            rel={'noopener noreferrer'}
            className={styles.originLink}
            title={'Open the product this image is borrowed from'}>
    {badge}
  </a>
}


function SectionHeader({title, subtitle, count}: {title: string, subtitle?: string, count: number}) {
  return <div className={styles.sectionHeader}>
    <div className={styles.sectionHeaderTitle}>
      {title}
      <Pill className={'ml-2'}>{count}</Pill>
    </div>
    {subtitle && <div className={styles.sectionHeaderSubtitle}>{subtitle}</div>}
  </div>
}


function ResolvedImagesTable({images}: {images: ResolvedImageItem[]}) {
  return <Table hover className={styles.resolvedTable}>
    <GrayTableHeader className={'d-flex'}>
      <GrayTableHeaderCell width={8}>Preview</GrayTableHeaderCell>
      <GrayTableHeaderCell width={10}>Image</GrayTableHeaderCell>
      <GrayTableHeaderCell width={14}>Classification</GrayTableHeaderCell>
      <GrayTableHeaderCell width={20}>Group</GrayTableHeaderCell>
      <GrayTableHeaderCell width={20}>Origin</GrayTableHeaderCell>
      <GrayTableHeaderCell width={18}>Links</GrayTableHeaderCell>
      <GrayTableHeaderCell width={10}>Tags</GrayTableHeaderCell>
    </GrayTableHeader>
    <tbody>
      {images.map(img => (
        <SimpleTableRow key={img.id} className={'d-flex'}>
          <SimpleTableCell width={8} className={'d-flex align-items-center'}>
            <div className={styles.resolvedThumbnail}>
              {img.links.thumbnail_small || img.links.thumbnail_medium ? (
                <img src={img.links.thumbnail_small || img.links.thumbnail_medium || undefined} alt={"Product"}/>
              ) : <ProductImagePlaceholder size={'sm'}/>}
            </div>
          </SimpleTableCell>

          <SimpleTableCell width={10} className={'d-flex align-items-center'}>
            <div className={styles.cellStack}>
              <span className={styles.monoText}>{img.id}</span>
              <span className={styles.mutedSmall}>group {img.group_id}</span>
            </div>
          </SimpleTableCell>

          <SimpleTableCell width={14} className={'d-flex align-items-center'}>
            <div className={styles.badgeStack}>
              <Badge color={'info'}>{img.orientation}</Badge>
              <Badge color={'primary'}>{img.type}</Badge>
            </div>
          </SimpleTableCell>

          <SimpleTableCell width={20} className={'d-flex align-items-center'}>
            <div className={styles.cellStack}>
              <div className={styles.tagRow}>
                <span className={styles.tagKey}>Style</span>
                <span className={styles.tagValue}>{img.group_style_number || "-"}</span>
              </div>
              <div className={styles.tagRow}>
                <span className={styles.tagKey}>Color</span>
                <span className={styles.tagValue}>
                  {img.group_color_name || "-"}{img.group_color_code ? ` (${img.group_color_code})` : ""}
                </span>
              </div>
              <div className={styles.tagRow}>
                <span className={styles.tagKey}>Group orientation</span>
                <span className={styles.tagValue}>{img.group_orientation || "-"}</span>
              </div>
              <div className={styles.tagRow}>
                <span className={styles.tagKey}>Collection</span>
                <span className={styles.tagValue}>{img.group_collection_id != null ? img.group_collection_id : "-"}</span>
              </div>
            </div>
          </SimpleTableCell>

          <SimpleTableCell width={20} className={'d-flex align-items-center'}>
            <div className={styles.cellStack}>
              <OriginIndicator img={img}/>
              <div className={styles.tagRow}>
                <span className={styles.tagKey}>Unified</span>
                <span className={styles.monoText}>{img.unified_product_id || "-"}</span>
              </div>
              <div className={styles.tagRow}>
                <span className={styles.tagKey}>Product</span>
                <span className={styles.monoText}>{img.product_id || "-"}</span>
              </div>
              {img.is_borrowed && <div className={styles.tagRow}>
                <span className={styles.tagKey}>Borrowed from</span>
                <span className={styles.monoText}>{img.borrowed_from_unified_product_id || "-"}</span>
              </div>}
            </div>
          </SimpleTableCell>

          <SimpleTableCell width={18} className={'d-flex align-items-center'}>
            <div className={styles.cellStack}>
              <LinkIndicator href={img.links.hi_res} label="Hi-res" withCopy/>
              <LinkIndicator href={img.links.image} label="Image" withCopy/>
              <LinkIndicator href={img.links.thumbnail_small} label="Thumb (S)" withCopy/>
              <LinkIndicator href={img.links.thumbnail_medium} label="Thumb (M)" withCopy/>
              <LinkIndicator href={img.links.source} label="Source" withCopy/>
            </div>
          </SimpleTableCell>

          <SimpleTableCell width={10} className={'d-flex align-items-center'}>
            <TagsDisplay tags={img.tags}/>
          </SimpleTableCell>
        </SimpleTableRow>
      ))}
    </tbody>
  </Table>
}


type ImagesModalProps = {
  unifiedId: string,
  isOpen: boolean,
  toggle: () => void
}

export default function ImagesModal({unifiedId, isOpen, toggle}: ImagesModalProps) {
  const [data, setData] = useState<ImagesApiResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setLoading(true);
    setError(null);
    setData(null);

    HttpService.GET<ImagesApiResponse>('/api/v1/images/', {unified: unifiedId})
      .then((response) => {
        if (response.status === 'success') {
          setData(response);
        } else {
          setError("Unexpected response status: " + response.status);
        }
      })
      .catch((err) => {
        setError(err.message || "Failed to fetch images");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isOpen, unifiedId]);

  const renderContent = () => {
    if (error) {
      return <ErrorAlert error={error}/>;
    }

    if (loading || !data) {
      return <Loading/>;
    }

    const groups = data.groups || [];
    const images = data.images || [];
    const resolvedImages = data.resolved_images || [];

    const directImageIds = new Set(images.map(img => img.id));
    const visibleResolvedImages = resolvedImages.filter(img => !directImageIds.has(img.id));
    const hiddenResolvedCount = resolvedImages.length - visibleResolvedImages.length;

    if (groups.length === 0 && resolvedImages.length === 0) {
      return <NoContentAvailable
        title={"No images yet"}
        subtitle={"This product has no images attached. Add some first and they will show up here."}
        className={'py-5'}/>;
    }

    return <div>
      <div className={styles.metaBar}>
        <Pill className={'mr-1'}>GTIN "{data.gtin || "not set"}"</Pill>
        <Pill color={data.gtin_centralized_images_active ? 'primary' : 'secondary'}>
          Centralized GTIN images {data.gtin_centralized_images_active ? "active" : "inactive"}
        </Pill>
      </div>

      <SectionHeader title={"Image groups"} count={groups.length}
                     subtitle={"Images directly attached to this product, organized by group."}/>

      {groups.length === 0
        ? <NoContentAvailable
          title={"No image groups"}
          subtitle={"There are no image groups attached directly to this product."}
          className={'py-4'}/>
        : groups.map(group => {
          const groupImages = images.filter(img => img.group_id === group.id);

          return <div key={group.id} className={styles.imageGroup}>
            <div className={styles.groupHeader}>
              <span className={'mx-1 font-weight-bold'}>{groupImages.length} images</span>
              <Pill className={'mx-1'}>Style number "{group.style_number}"</Pill>
              <Pill className={'mx-1'}>Color name "{group.color_name}"</Pill>
              <Pill className={'mx-1'}>Color code "{group.color_code}"</Pill>
            </div>

            <div className={styles.imageCards}>
              {groupImages.map((img, index) => {
                return <div key={img.id} className={styles.imageCard}>
                  <div className={styles.cardThumbnail}>
                    {img.links.thumbnail_medium ? (
                      <img src={img.links.thumbnail_medium} alt={"Product"}/>
                    ) : <ProductImagePlaceholder size={'mdLg'}/>}
                  </div>

                  <div className={styles.cardContent}>
                    <div className={styles.cardBadges}>
                      <span className={'mr-2 font-weight-bold'}>#{index + 1}</span>
                      <Badge color={'info'}>{img.orientation}</Badge>
                      <Badge color={'primary'}>{img.type}</Badge>
                    </div>

                    <div className={styles.cardSection}>
                      <div className={styles.sectionTitle}>Links</div>
                      <div className={styles.linksGrid}>
                        <LinkIndicator href={img.links.hi_res} label="Hi-res" withCopy/>
                        <LinkIndicator href={img.links.image} label="Image" withCopy/>
                        <LinkIndicator href={img.links.thumbnail_small} label="Thumb (S)" withCopy/>
                        <LinkIndicator href={img.links.thumbnail_medium} label="Thumb (M)" withCopy/>
                        <LinkIndicator href={img.links.source} label="Source" withCopy/>
                      </div>
                    </div>

                    <div className={styles.cardSection}>
                      <div className={styles.sectionTitle}>Tags</div>
                      <TagsDisplay tags={img.tags}/>
                    </div>

                    <div className={styles.cardSection}>
                      <div className={styles.sectionTitle}>Cached AI Labels</div>
                      <AiLabelsDisplay labels={img.cached_ai_labels}/>
                    </div>
                  </div>
                </div>
              })}
            </div>
          </div>
        })}

      <div className={styles.resolvedSection}>
        <SectionHeader title={"Resolved images"} count={visibleResolvedImages.length}
                       subtitle={"The images actually served for this product, including images borrowed from other unified products."}/>

        {hiddenResolvedCount > 0 && <div className={styles.hiddenResolvedNotice}>
          <i className={'fa-solid fa-circle-info mr-2'}/>
          {hiddenResolvedCount} resolved {hiddenResolvedCount === 1 ? "image" : "images"} hidden due to being directly assigned
        </div>}

        {visibleResolvedImages.length === 0
          ? <NoContentAvailable
            title={hiddenResolvedCount > 0 ? "Nothing extra resolved" : "No resolved images"}
            subtitle={hiddenResolvedCount > 0
              ? "Every resolved image is already shown above as directly assigned."
              : "No images could be resolved for this product."}
            className={'py-4'}/>
          : <ResolvedImagesTable images={visibleResolvedImages}/>}
      </div>
    </div>
  };

  return <GrayModal
    isOpen={isOpen}
    toggle={toggle}
    style={{minWidth: "80rem"}}
    title={'Images'}
    secondaryHeaderButton={{
      text: 'Close',
      onClick: toggle
    }}
    bodyContent={<div className={'p-3'}>{renderContent()}</div>}
  />
}
